<template>
  <div>
    <v-app-bar
      v-if="teacherMode"
      class="app_bar_contained"
      color="transparent"
      dense
      flat
      @click.alt="showEvents = !showEvents"
    >
      <v-btn
        @click="back"
        icon
      >
        <v-icon>arrow_back</v-icon>
      </v-btn>

      <v-list
        color="transparent"
        class="ml-4"
      >
        <v-list-item-content>
          <span
            v-if="teacherMode && score"
            :class="{ 'blur-names': blurNames}"
          >
            {{ score.name }}
            <time-accommodation-chip
              x-small
              :time-accommodation="timeAccommodation"
              v-if="timeAccommodation.id && quiz?.time_accommodation"
            />
          </span>
          <div
            v-if="teacherMode && score"
            class="caption d-inline-flex align-center d-flex"
          >
            <span :class="{ 'blur-names': blurNames}">
              {{ score.email }}
            </span>

            <template v-if="teacherMode && score && !paperQuiz">
              <div class="ml-2  d-flex">
                <v-icon
                  class="mr-1"
                  x-small
                >
                  mdi-clock-time-four-outline
                </v-icon>
                <span>
                  {{ helpers.scoreDuration(score.exam_duration) }}
                </span>
                <v-icon
                  class="mr-1 ml-2"
                  x-small
                >
                  mdi-calendar-clock-outline
                </v-icon>
                <span>
                  {{ timeHelpers.formatDateRange(score.started_at, score.finished_at) }}
                </span>
              </div>
              <user-devices
                class="ml-2"
                :devices="score?.devices"
                :multiple-monitor-confidence="score.cheat.multiple_monitor_confidence"
                v-if="score.devices"
                x-small
              />
            </template>
          </div>
          <span v-if="!teacherMode">
            {{ quiz && quiz.name || this.$t('live.correction.loading') }}
          </span>
        </v-list-item-content>
      </v-list>
      <v-spacer />

      <v-badge
        :content="totalMessagesCount"
        :value="totalMessagesCount > 0"
        :color="unreadMessagesCount > 0 ? 'error' : 'blue-grey'"
        overlap
        offset-x="20"
        offset-y="13"
        v-if="rightDrawerCollapsed"
      >
        <eva-button
          icon="mdi-chat-outline"
          @click="openChatDrawer"
          class="mr-2"
          :disabled="!score"
          dense
          text
        />
      </v-badge>

      <score-chat-channel
        v-if="score && teacherMode"
        :score="score"
      />


      <eva-button
        v-if="score?.scanned_paper_quiz_sheet_id"
        dense
        text
        :to="{name: 'quizPaperScan', params: {quizId, scanId: score.scanned_paper_quiz_sheet_id}}"
        icon-left="mdi-file-outline"
        :label="$t('live.correction.openScan')"
      />
      <eva-button
        dense
        text
        :loading="downloadingCorrection"
        @click="downloadCopyWithCorrection"
        icon-left="mdi-file-download-outline"
        :label="$t('live.correction.download')"
      />
    </v-app-bar>

    <v-sheet color="transparent">
      <scoreCards
        v-if="score"
        :teacher-mode="teacherMode"
        :score="score"
        :quiz="quiz"
      />

      <cheat-score-alert
        :quiz="quiz"
        :teacher-mode="teacherMode"
      />

      <div class="mt-2">
        <evalmee-quiz-events-report-and-timeline
          v-if="score && teacherMode && quiz.quiz_type === 'exam' && score.events_report"
          :events-pairs="score.events_report?.events_pairs || []"
          :events="events"
          :events-reports="score.events_report?.report"
          :exercises="exercises"
          :student-screenshots="screenshots"
          :exercise-events-summaries="score.events_report?.exercises_details || []"
          class="mb-4"
          :quiz="quiz"
          :copy-or-cut-events-from-subject-ids="score.events_report?.copy_or_cut_events_from_subject_ids"
          :paste-events-without-copy-ids="score.events_report?.paste_events_without_copy_ids"
          :copy-or-cut-events-without-paste-ids="score.events_report?.copy_or_cut_events_without_paste_ids"
          @click:exercise="scrollToExercise"
          @fetch-screenshots="fetchScreenshots"
          :loading="loadingStudentEvents || loadingScreenshots"
        >
          <template #timelineAppend="{debug}">
            <v-divider class="mt-2" />

            <support-comment-editor
              :content="score.support_comment"
              @save="saveSupportComment"
              :read-only="!debug"
            >
              <template #append>
                <v-divider />
              </template>
            </support-comment-editor>

            <private-comment-editor
              :content="score.private_comment"
              @save="savePrivateComment"
            />
          </template>
        </evalmee-quiz-events-report-and-timeline>

        <evalmee-quiz-events-report
          :score="score"
          v-if="showEvents"
        />

        <evalmee-quiz-events
          v-if="showEvents"
          :score-id="score.id"
        />

        <copy-feedback
          v-if="score"
          :score="score"
          :quizzes-attempt-summary="quizzesAttemptSummary"
          :quiz="quiz"
          :teacher-mode="teacherMode"
          class="mb-12"
        />

        <v-alert
          v-if="teacherMode && !showLegacyCorrection"
          :icon="false"
          class="mb-5"
          text
          type="info"
          dense
        >
          {{ $t('live.correction.questionOrder') }}
        </v-alert>

        <out-of-time-several-alert
          v-if="quiz && score && currentUser.teacher"
          :answers="answers"
          :score="score"
          :quiz="quiz"
          :quiz-instance="quizInstance"
          class="mb-5"
        />


        <scan-modale
          v-if="paperQuiz"
          :quiz="quiz"
          :score="score"
        />

        <div
          v-if="!quiz"
          class="center"
        >
          <preloader />
        </div>

        <div v-else>
          <template v-if="showLoader">
            <v-skeleton-loader
              v-for="i in quiz.questions_count"
              :key="`sk-${i}`"
              class="mb-2"
              height="200"
              type="card"
            />
          </template>

          <div
            v-for="(section, sectionIndex) in quizInstanceSections"
            :key="section.id"
          >
            <span
              class="grey--text font-weight-medium text-body-2"
              v-if="quizInstanceSections.length > 1"
            >
              Section {{ sectionIndex + 1 }}
            </span>
            <katex-md
              class="text-h5 font-weight-medium"
              v-if="section.attributes.name"
              :expr="section.attributes.name"
            />

            <katex-md
              class="mt-2"
              v-if="section.attributes.description"
              :expr="section.attributes.description"
            />

            <template v-if="!currentCorrectionLoading">
              <v-lazy
                v-for="(exercise) in section.children"
                :key="exercise.uuid"
                :options="{threshold: .1}"
                class="mb-12"
                min-height="200"
                transition="fade-transition"
                :ref="`exercise-${exercise.id}`"
              >
                <div>
                  <exercise-student
                    correction-mode
                    :exercise="exercise"
                    :manual-correction-mode="teacherMode"
                    :quiz="quiz"
                    :quiz-instance="quizInstance"
                    :user-id="userId"
                    :quiz-score="score"
                    :section-index="sectionIndex"
                    :show-section-index="quizInstanceSections.length > 1"
                    exercise-list-mode
                  >
                    <template
                      #eventsSummary="{showSeenChip}"
                      v-if="teacherMode"
                    >
                      <exercise-events-summary
                        :events="eventsForExercise(exercise.id)"
                        :events-pairs="eventsPairsForExercise(exercise.id)"
                        :copy-or-cut-events-from-subject-ids="score.events_report?.copy_or_cut_events_from_subject_ids"
                        :paste-events-without-copy-ids="score.events_report?.paste_events_without_copy_ids"
                        :copy-or-cut-events-without-paste-ids="score.events_report?.copy_or_cut_events_without_paste_ids"
                        class="mr-1"
                        :show-seen-chip="showSeenChip"
                        :exercise-events-summary="eventsSummaryForExercise(exercise.id)"
                        :quiz="quiz"
                      >
                        <template #appendIfAny>
                          <v-divider
                            class="mx-2"
                            vertical
                          />
                        </template>
                      </exercise-events-summary>
                    </template>
                    <template #grader="{answer, question}">
                      <open-question-grader
                        v-if="answer"
                        :answer="answer"
                        :current-user="currentUser"
                        :label="$t('live.correction.manual')"
                        :question="question"
                        :quiz="quiz"
                        class="mb-2"
                      />
                    </template>
                    <template #feedbackEditor="{answer, question, questionComment, hasQuestionComment}">
                      <v-card
                        flat
                        outlined
                        v-if="answer || hasQuestionComment"
                      >
                        <question-correction-comment
                          v-if="hasQuestionComment"
                          :content="questionComment"
                        />
                        <v-divider v-if="hasQuestionComment" />
                        <answer-feedback-editor
                          v-if="answer"
                          :answer="answer"
                          :question="question"
                          :quiz="quiz"
                        />
                      </v-card>
                    </template>
                  </exercise-student>
                </div>
              </v-lazy>
            </template>
          </div>

          <!--          Legacy correction    -->
          <legacyCorrection
            v-if="showLegacyCorrection"
            :answer-by-question-and-user-id="answerByQuestionAndUserId"
            :current-user="currentUser"
            :questions="questions"
            :quiz="quiz"
            :teacher-mode="teacherMode"
            :user-id="userId"
          />
          <!--          End Legacy correction    -->
        </div>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import preloader from "../../components/shared/preloader.vue"
import ScanModale from "../../components/quiz/paper/scan_modale.vue"
import EvalmeeQuizEvents from "./evalmee_quiz_events.vue"
import OpenQuestionGrader from "../../components/questions/open_question_grader.vue"
import CopyFeedback from "../../components/questions/CopyFeedback.vue"
import ExerciseStudent from "../../components/quiz/editor/preview/exercise_preview.vue"
import instanceExerciseForMixin from "../../components/quiz/evalmee/instance_exercise_for_mixin"
import OutOfTimeSeveralAlert from "../../components/questions/out_of_time_several_alert.vue"
import CheatScoreAlert from "./cheat-score-alert.vue"
import LegacyCorrection from "./legacy-correction.vue"
import helpers from "../../helpers"
import ScoreCards from "./ScoreCards.vue"
import KatexMd from "../../components/katex-md.vue"
import axiosHelpers from "../../helpers/axios_helpers"
import EvalmeeQuizEventsReport from "./evalmee_quiz_events_report.vue"
import GetFeatureMixin from "../../components/shared/get_feature_mixin"
import EvalmeeQuizEventsReportAndTimeline from "./events-timeline/evalmeeQuizEventsReportAndTimeline.vue"
import UserDevices from "./user_devices/user_devices.vue"
import TimeAccommodationChip from "@/components/quiz/time_accommodations/TimeAccommodationChip.vue"
import { useScore } from "@/composables/useScore"
import { blurNames } from "@/composables/useDevUtils"
import timeHelpers from "../../helpers/time_helpers"
import ws from "@/web_sockets"
import AnswerFeedbackEditor from "@/components/questions/answerFeedbackEditor.vue"
import QuestionCorrectionComment from "@/components/questions/question_correction_comment.vue"
import PrivateCommentEditor from "@/components/quiz/correction/privateCommentEditor.vue"
import ExerciseEventsSummary from "@/components/quiz/correction/exerciseEventsSummary.vue"
import { useScroll } from "@/composables/useScroll"
import ScoreChatChannel from "@/components/quiz/evalmee/ScoreChatChannel.vue"
import SupportCommentEditor from "@/components/quiz/correction/supportCommentEditor.vue"
export default {
  name: "QuizCorrection",
  props: {
    scoreId: { type: Number, default: null },
    quizId: { type: Number, default: null },
    quizzesAttemptSummaryId: { type: Number, default: null },
    teacherMode: { type: Boolean, default: false },
    teacherBackRouteName: { type: String, default: "quizTeacherScores" },
    rightDrawerCollapsed: { type: Boolean, default: true },
  },
  mixins: [instanceExerciseForMixin, GetFeatureMixin],
  setup() {
    return {
      useScore,
      blurNames: blurNames(),
    }
  },
  data() {
    return {
      scanModal: false,
      scoreFetched: false,
      showEvents: false,
      state: "",
      downloadingCorrection: false,
      loadingStudentEvents: false,
      loadingScreenshots: false,
    }
  },

  components: {
    ExerciseEventsSummary,
    PrivateCommentEditor,
    SupportCommentEditor,
    QuestionCorrectionComment,
    AnswerFeedbackEditor,
    TimeAccommodationChip,
    UserDevices,
    EvalmeeQuizEventsReportAndTimeline,
    EvalmeeQuizEventsReport,
    ScoreCards,
    LegacyCorrection,
    CheatScoreAlert,
    OutOfTimeSeveralAlert,
    ExerciseStudent,
    EvalmeeQuizEvents,
    OpenQuestionGrader,
    ScanModale,
    preloader,
    CopyFeedback,
    KatexMd,
    ScoreChatChannel,
  },

  methods: {
    ...mapActions([
      "fetchScoresByQuiz",
      "fetchScore",
      "fetchQuiz",
      "fetchAnswers",
      "fetchMyInstanceByQuiz",
      "fetchInstancesByQuiz",
      "fetchPaperSheet",
      "fetchQuestionsByQuiz",
      "fetchScoreSummary",
      "updateScoreBySummary",
      "fetchScreenshotsByScore",
      "fetchStudentEventsByScore",
      "newAppNotification",
    ]),
    fetchScores() {
      if (!this.quiz) { return }
      if (this.scoreFetched) return

      // We cannot only check is score is present because
      // a score collections includes less fields
      this.scoreId ?
        this.fetchScoreSummary({ scoreId: this.scoreId }) :
        this.fetchScoresByQuiz(this.quiz)

      if (this.questions?.length === 0) {
        this.fetchQuestionsByQuiz(this.quiz)
      }
      this.scoreFetched = true
    },
    downloadCopyWithCorrection() {
      this.downloadingCorrection = true

      axiosHelpers.downloadFile({
        url: this.score.links.export_copy_with_correction,
        filename: `${this.quiz.name}_${this.score.name}.pdf`,
      }).then(() => this.downloadingCorrection = false)
    },

    fetchCorrection() {
      if (!this.quiz) { return }
      if (!this.userId) { return }
      if (this.state === "loading") return
      if (this.state === "loaded") return

      this.loading()

      if (this.quiz.quiz_type === "paper") {
        return this.loaded()
        // Dorian - 2024-10-13
        // Code bellow is temporary disabled after PaperQuizSheet migration to use subscription_id
        // instead of user_id

        // return this.fetchPaperSheet({
        //   quiz: this.quiz,
        //   userIds: [this.userId],
        // }).then(this.loaded)
      }

      if (this.teacherMode) {
        this.fetchInstancesByQuiz({
          quiz: this.quiz,
          userIds: [this.userId],
        }).then(this.loaded)
      } else {
        this.fetchMyInstanceByQuiz({
          quiz: this.quiz,
          quizzesAttemptSummary: this.quizzesAttemptSummary,
        }).then(this.loaded)
      }

    },

    loaded() {
      this.state = "loaded"
    },

    loading() {
      this.state = "loading"
    },

    back() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push(this.backRoute)
      }
    },

    // DUPLICATE-#5
    updateScoresLiveUpdateSubscriptions(scoreIds) {
      if(ws.subscriptions.TeachersScores) {
        return ws.subscriptions.TeachersScores.followScores(scoreIds)
      }
      ws.channels.TeachersScores.subscribe({ quizId: this.quiz.id, scoreIds })
    },
    savePrivateComment(privateComment) {
      this.updateScoreBySummary({ ...this.score, private_comment: privateComment })
        .then( () => this.newAppNotification({
          message: this.$t("quiz.edit.toast.successMessage"),
          type: "success",
        })
        ).catch((error) =>
          this.newAppNotification({
            message: this.$t("quiz.edit.toast.errorMessage", { error }),
            type: "error",
          })
        )
    },
    saveSupportComment(supportComment) {
      this.updateScoreBySummary({ ...this.score, support_comment: supportComment })
        .then( () => this.newAppNotification({
          message: this.$t("quiz.edit.toast.successMessage"),
          type: "success",
        })
        ).catch((error) =>
          this.newAppNotification({
            message: this.$t("quiz.edit.toast.errorMessage", { error }),
            type: "error",
          })
        )
    },
    scrollToExercise(id){
      const el = this.$refs[`exercise-${id}`]
      if(!el) {return}

      // use "instant" to avoid the scroll issue with lazy loading
      useScroll().intoView(el[0].$el, { offset: 100, behavior: "instant" })
    },
    openChatDrawer() {
      this.$emit("openRightDrawer", "preview")
    },
    fetchScreenshots({ timeWindow, maxVisibleScreenshots }) {
      this.loadingScreenshots = true
      this.fetchScreenshotsByScore({ score: this.score, timeWindow, maxVisibleScreenshots })
        .finally(() => this.loadingScreenshots = false)
    },
    fetchStudentEvents() {
      this.loadingStudentEvents = true
      this.fetchStudentEventsByScore(this.score)
        .finally(() => this.loadingStudentEvents = false)
    },
  },

  mounted() {
    if (this.quiz == null) {this.fetchQuiz(this.quizId)}
  },
  watch: {
    quiz: {
      handler() {this.fetchScores()},
      immediate: true,
    },
    userId: {
      handler() {this.fetchCorrection()},
      immediate: true,
    },
    score: {
      handler(val) {
        if (!val?.id) return
        if(this.teacherMode) {
          this.updateScoresLiveUpdateSubscriptions([val.id])
          // this.fetchScreenshots()
          this.fetchStudentEvents()
        } else {
          this.fetchCorrection()
        }
      },
      immediate: true,
    },
  },

  computed: {
    helpers() {
      return helpers
    },
    timeHelpers() {
      return timeHelpers
    },
    // lazyLoading() {
    //   return !this.teacherMode
    // },
    ...mapGetters([
      "quizById",
      "questionsByQuizId",
      "currentCorrectionLoading",
      "getUserById",
      "scoreByQuizAndUserId",
      "currentUser",
      "scoreById",
      "answersByQuestionsAndUserId",
      "answerByQuestionAndUserId",
      "quizInstanceByQuizId2",
      "studentEvents",
      "quizzesAttemptSummaryById",
      "studentScreenshots",
      "scoresByQuizId",
      "scoresLoading",
      "quizzesSubscriptions",
      "quizzesGroups",
      "channelByQuizAndUserId",
      "unreadMessagesOnChannel",
      "messages",
    ]),
    quiz() { return this.quizById(parseInt(this.quizId)) },
    questions() {
      return this.questionsByQuizId(this.quizId).sort((a, b) => a.id - b.id)
    },
    events() {
      const answersEvents = this.answers.map(a => ({
        certified: true,
        happened_at: a.last_edit_at,
        score_id: this.score.id,
        kind: "answers_last_update",
        quizzes_exercise_id: this.questions.find(q => q.id === a.question_id)?.quizzes_exercise_id,
        details: {
          questionName: this.questions.find(q => q.id === a.question_id)?.name,
        },
      }))

      return [...this.studentEvents(this.score.id), ...answersEvents]
    },

    eventsForExercise() {
      return (exerciseId) => this.events.filter(e => e.quizzes_exercise_id === exerciseId)
    },
    eventsPairsForExercise() {
      return (exerciseId) => this.score?.events_report?.events_pairs?.filter(e => e.exercise_ids.includes(exerciseId)) || []
    },
    eventsSummaryForExercise() {
      return (exerciseId) => this.score?.events_report?.exercises_details?.find(e => e.exercise_id === exerciseId)
    },
    backRoute() {
      if (this.teacherMode) {
        return {
          name: this.teacherBackRouteName,
          params: { quizId: this.quizId },
        }
      } else {
        return { name: "examsIndex" }
      }
    },
    userId() {
      if (this.scoreId) {
        return this.score?.user_id
      } else {
        return this.currentUser?.id
      }
    },
    score() {
      if (this.scoreId) {
        return this.scoreById(this.scoreId)
      } else {
        return this.scoreByQuizAndUserId(this.quizId, this.userId)
      }
    },
    timeAccommodation(){
      return this.useScore(this.score).timeAccommodation.value
    },
    paperQuiz() { return this?.quiz?.quiz_type === "paper"},
    answers() {
      return this.answersByQuestionsAndUserId(
        this.questions?.map(q => q.id),
        this.userId
      )
    },

    showLoader() {
      if (this.exercises.length > 0) return false
      return this.currentCorrectionLoading || this.instancesLoading
    },

    studentExamDuration() {
      if (!this.score.exam_duration) return null

      return helpers.scoreDuration(this.score.exam_duration)
    },
    showLegacyCorrection() {
      return !this.currentCorrectionLoading
        && !this.instancesLoading
        && this.paperQuiz
    },
    quizzesAttemptSummary() {
      return this.quizzesAttemptSummaryById(this.quizzesAttemptSummaryId)
    },
    screenshots() {
      return this.studentScreenshots(this.score.id)
    },
    // DUPLICATE-#18
    channel() {
      return this.channelByQuizAndUserId(this.quiz?.id, this.score?.user_id)
    },
    unreadMessagesCount() {
      if (!this.channel) return 0
      return this.unreadMessagesOnChannel(this.channel).length
    },
    totalMessagesCount() {
      if (!this.channel) return 0
      return this.messages(this.channel.id).length
    },
  },
}
</script>
